import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CardContent,
    Checkbox,
    FormControlLabel,
    Grid,
    IconButton,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import { useParams, useHistory } from 'react-router-dom';
import { IconBox, IconDeviceFloppy, IconTrash, IconEdit, IconCheck, IconX } from '@tabler/icons';
import api from '../../../../services/api';
import PageHeader from '../../../../../ui-component/extended/PageHeader';
import GlassCard from '../../../../../ui-component/extended/GlassCard';

const ORIGIN_OPTIONS = [
    { value: '0', label: '0 - Nacional' },
    { value: '1', label: '1 - Estrangeira - importação direta' },
    { value: '2', label: '2 - Estrangeira - mercado interno' },
    { value: '3', label: '3 - Nacional - conteúdo importado > 40%' },
    { value: '4', label: '4 - Nacional - processos produtivos básicos' },
    { value: '5', label: '5 - Nacional - conteúdo importado <= 40%' },
    { value: '6', label: '6 - Estrangeira - importação direta, sem similar nacional' },
    { value: '7', label: '7 - Estrangeira - mercado interno, sem similar nacional' },
    { value: '8', label: '8 - Nacional - conteúdo importado > 70%' }
];

const EditProduct = () => {
    const { id } = useParams();
    const history = useHistory();

    const [appId, setAppId] = useState(null);
    const [product, setProduct] = useState(null);
    const [variacoes, setVariacoes] = useState([]);
    const [categories, setCategories] = useState([]);
    const [brands, setBrands] = useState([]);
    const [models, setModels] = useState([]);
    const [units, setUnits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [status, setStatus] = useState({ type: '', message: '' });
    const [editingVariacaoId, setEditingVariacaoId] = useState(null);
    const [variacaoDraft, setVariacaoDraft] = useState({});

    useEffect(() => {
        (async () => {
            const res = await api.get('v1/apps/default/');
            setAppId(res.data.app.id);
        })();
    }, []);

    useEffect(() => {
        if (!appId || !id) return;
        loadProduct();
        loadClassificationData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId, id]);

    const loadProduct = async () => {
        try {
            const res = await api.get(`v1/apps/${appId}/products/${id}/`);
            setProduct(res.data.product);
            setVariacoes(res.data.product.variacao || []);
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao carregar produto' });
        } finally {
            setLoading(false);
        }
    };

    const loadClassificationData = async () => {
        const [categoriesRes, brandsRes, modelsRes, unitsRes] = await Promise.all([
            api.get(`v1/apps/${appId}/categories/`),
            api.get(`v1/apps/${appId}/brands/`),
            api.get(`v1/apps/${appId}/product-models/`),
            api.get(`v1/apps/${appId}/units-of-measure/`)
        ]);
        setCategories(categoriesRes.data || []);
        setBrands(brandsRes.data || []);
        setModels(modelsRes.data || []);
        setUnits(unitsRes.data || []);
    };

    const handleChange = (field, value) => setProduct({ ...product, [field]: value });

    const save = async () => {
        setStatus({ type: '', message: '' });
        setSaving(true);
        try {
            await api.patch(`v1/apps/${appId}/products/${id}/`, {
                nome: product.nome,
                category: product.category || null,
                brand: product.brand || null,
                model: product.model || null,
                unit_of_measure: product.unit_of_measure || null,
                ncm: product.ncm,
                cest: product.cest,
                origin: product.origin,
                ativo: product.ativo
            });
            setStatus({ type: 'success', message: 'Produto atualizado com sucesso!' });
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao salvar produto' });
        } finally {
            setSaving(false);
        }
    };

    const deleteProduct = async () => {
        if (!window.confirm(`Excluir o produto "${product.nome}"?`)) return;
        try {
            await api.delete(`v1/apps/${appId}/products/${id}/`);
            history.push('/dashboard/config/products');
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao excluir produto' });
        }
    };

    const startEditVariacao = (variacao) => {
        setEditingVariacaoId(variacao.id);
        setVariacaoDraft({ ...variacao });
    };

    const cancelEditVariacao = () => {
        setEditingVariacaoId(null);
        setVariacaoDraft({});
    };

    const saveVariacao = async () => {
        try {
            const { id: variacaoId, imagens, ...payload } = variacaoDraft;
            const res = await api.patch(`v1/apps/${appId}/variacoes/${variacaoId}/`, payload);
            setVariacoes((prev) => prev.map((v) => (v.id === variacaoId ? res.data : v)));
            setEditingVariacaoId(null);
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao salvar variação' });
        }
    };

    const deleteVariacao = async (variacao) => {
        if (!window.confirm(`Excluir a variação "${variacao.nome_variacao}"?`)) return;
        try {
            await api.delete(`v1/apps/${appId}/variacoes/${variacao.id}/`);
            setVariacoes((prev) => prev.filter((v) => v.id !== variacao.id));
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao excluir variação' });
        }
    };

    if (loading || !product) return null;

    return (
        <Box>
            <PageHeader icon={IconBox} gradient="blue" title={product.nome || 'Produto'} subtitle="Editar dados do produto" />

            {status.message && (
                <Alert severity={status.type} sx={{ mb: 2 }}>
                    {status.message}
                </Alert>
            )}

            <GlassCard sx={{ mb: 3 }}>
                <CardContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField label="Nome" fullWidth value={product.nome || ''} onChange={(e) => handleChange('nome', e.target.value)} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                select
                                label="Categoria"
                                fullWidth
                                value={product.category || ''}
                                onChange={(e) => handleChange('category', e.target.value)}
                            >
                                <MenuItem value="">Nenhuma</MenuItem>
                                {categories.map((c) => (
                                    <MenuItem key={c.id} value={c.id}>
                                        {c.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                select
                                label="Marca"
                                fullWidth
                                value={product.brand || ''}
                                onChange={(e) => handleChange('brand', e.target.value)}
                            >
                                <MenuItem value="">Nenhuma</MenuItem>
                                {brands.map((b) => (
                                    <MenuItem key={b.id} value={b.id}>
                                        {b.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                select
                                label="Modelo"
                                fullWidth
                                value={product.model || ''}
                                onChange={(e) => handleChange('model', e.target.value)}
                            >
                                <MenuItem value="">Nenhum</MenuItem>
                                {models.map((m) => (
                                    <MenuItem key={m.id} value={m.id}>
                                        {m.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                select
                                label="Unidade de Medida"
                                fullWidth
                                value={product.unit_of_measure || ''}
                                onChange={(e) => handleChange('unit_of_measure', e.target.value)}
                            >
                                <MenuItem value="">Nenhuma</MenuItem>
                                {units.map((u) => (
                                    <MenuItem key={u.id} value={u.id}>
                                        {u.code} - {u.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField label="NCM" fullWidth value={product.ncm || ''} onChange={(e) => handleChange('ncm', e.target.value)} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField label="CEST" fullWidth value={product.cest || ''} onChange={(e) => handleChange('cest', e.target.value)} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                select
                                label="Origem"
                                fullWidth
                                value={product.origin || ''}
                                onChange={(e) => handleChange('origin', e.target.value)}
                            >
                                <MenuItem value="">Nenhuma</MenuItem>
                                {ORIGIN_OPTIONS.map((o) => (
                                    <MenuItem key={o.value} value={o.value}>
                                        {o.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={<Checkbox checked={!!product.ativo} onChange={(e) => handleChange('ativo', e.target.checked)} />}
                                label="Produto ativo"
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Stack direction="row" justifyContent="flex-end" spacing={2}>
                                <Button color="error" startIcon={<IconTrash size={18} />} onClick={deleteProduct}>
                                    Excluir produto
                                </Button>
                                <Button onClick={() => history.push('/dashboard/config/products')}>Voltar</Button>
                                <Button variant="contained" startIcon={<IconDeviceFloppy size={18} />} onClick={save} disabled={saving}>
                                    {saving ? 'Salvando...' : 'Salvar'}
                                </Button>
                            </Stack>
                        </Grid>
                    </Grid>
                </CardContent>
            </GlassCard>

            <GlassCard>
                <CardContent>
                    <Typography variant="h6" fontWeight={700} mb={2}>
                        Variações
                    </Typography>

                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Nome</TableCell>
                                <TableCell>SKU</TableCell>
                                <TableCell>Cód. Barras</TableCell>
                                <TableCell align="right">Estoque</TableCell>
                                <TableCell align="right">Custo</TableCell>
                                <TableCell align="right">Venda</TableCell>
                                <TableCell align="right">Peso (kg)</TableCell>
                                <TableCell>Controle</TableCell>
                                <TableCell align="right" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {variacoes.map((v) => {
                                const isEditing = editingVariacaoId === v.id;
                                return (
                                    <TableRow key={v.id}>
                                        <TableCell>
                                            {isEditing ? (
                                                <TextField
                                                    size="small"
                                                    value={variacaoDraft.nome_variacao || ''}
                                                    onChange={(e) => setVariacaoDraft({ ...variacaoDraft, nome_variacao: e.target.value })}
                                                />
                                            ) : (
                                                v.nome_variacao
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {isEditing ? (
                                                <TextField
                                                    size="small"
                                                    value={variacaoDraft.sku || ''}
                                                    onChange={(e) => setVariacaoDraft({ ...variacaoDraft, sku: e.target.value })}
                                                />
                                            ) : (
                                                v.sku || '—'
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {isEditing ? (
                                                <TextField
                                                    size="small"
                                                    value={variacaoDraft.barcode || ''}
                                                    onChange={(e) => setVariacaoDraft({ ...variacaoDraft, barcode: e.target.value })}
                                                />
                                            ) : (
                                                v.barcode || '—'
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            {isEditing ? (
                                                <TextField
                                                    size="small"
                                                    value={variacaoDraft.estoque ?? ''}
                                                    onChange={(e) => setVariacaoDraft({ ...variacaoDraft, estoque: e.target.value })}
                                                />
                                            ) : (
                                                v.estoque
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            {isEditing ? (
                                                <TextField
                                                    size="small"
                                                    value={variacaoDraft.valor_custo ?? ''}
                                                    onChange={(e) => setVariacaoDraft({ ...variacaoDraft, valor_custo: e.target.value })}
                                                />
                                            ) : (
                                                `R$ ${v.valor_custo}`
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            {isEditing ? (
                                                <TextField
                                                    size="small"
                                                    value={variacaoDraft.valor_venda ?? ''}
                                                    onChange={(e) => setVariacaoDraft({ ...variacaoDraft, valor_venda: e.target.value })}
                                                />
                                            ) : (
                                                `R$ ${v.valor_venda}`
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            {isEditing ? (
                                                <TextField
                                                    size="small"
                                                    value={variacaoDraft.weight ?? ''}
                                                    onChange={(e) => setVariacaoDraft({ ...variacaoDraft, weight: e.target.value })}
                                                />
                                            ) : (
                                                v.weight || '—'
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {isEditing ? (
                                                <TextField
                                                    select
                                                    size="small"
                                                    value={variacaoDraft.controle_estoque || 'none'}
                                                    onChange={(e) => setVariacaoDraft({ ...variacaoDraft, controle_estoque: e.target.value })}
                                                    sx={{ minWidth: 130 }}
                                                >
                                                    <MenuItem value="none">Nenhum</MenuItem>
                                                    <MenuItem value="lot">Lote</MenuItem>
                                                    <MenuItem value="serial">Número de Série</MenuItem>
                                                </TextField>
                                            ) : (
                                                { none: 'Nenhum', lot: 'Lote', serial: 'Número de Série' }[v.controle_estoque] || 'Nenhum'
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            {isEditing ? (
                                                <>
                                                    <IconButton size="small" onClick={saveVariacao}>
                                                        <IconCheck size={16} />
                                                    </IconButton>
                                                    <IconButton size="small" onClick={cancelEditVariacao}>
                                                        <IconX size={16} />
                                                    </IconButton>
                                                </>
                                            ) : (
                                                <>
                                                    <IconButton size="small" onClick={() => startEditVariacao(v)}>
                                                        <IconEdit size={16} />
                                                    </IconButton>
                                                    <IconButton size="small" onClick={() => deleteVariacao(v)}>
                                                        <IconTrash size={16} />
                                                    </IconButton>
                                                </>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            {variacoes.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={9} align="center">
                                        Nenhuma variação cadastrada
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </GlassCard>
        </Box>
    );
};

export default EditProduct;
